import { useState } from 'react';
import { CustomerSheet } from '@/features/customers/CustomerSheet';
import { usePermissions } from '@/hooks/usePermissions';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Scale, Pencil } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { PageHeader } from '@/app/DashboardLayout';
import { formatDate, financialYearPeriod, financialYearOf, today, type Period } from '@/lib/dates';
import { useFiscalYearStartMonth } from '@/hooks/useOrgSettings';
import {
  Badge, Button, Card, EmptyState, ErrorState, Money, PeriodPicker, SkeletonTable,
  TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';

interface LedgerEntry {
  date: string;
  type: 'opening_balance' | 'invoice' | 'payment' | 'credit_note' | 'write_off';
  reference: string;
  description: string;
  debit: string;
  credit: string;
  balance: string;
  entityId: string | null;
}

interface CustomerLedger {
  customer: { id: string; name: string; gstin: string | null; currency: string };
  period: { from: string | null; to: string | null };
  /** Every currency the customer has a document or payment in; the statement shown is `customer.currency`. */
  currencies: string[];
  entries: LedgerEntry[];
  totals: { invoiced: string; received: string; credited: string; writtenOff: string; closing: string };
}

const TYPE_LABEL: Record<LedgerEntry['type'], string> = {
  opening_balance: 'Opening balance',
  invoice: 'Invoice',
  payment: 'Payment',
  credit_note: 'Credit note',
  write_off: 'Written off',
};

/**
 * One customer's account, in date order, with a running balance.
 *
 * The page opened in every dispute. It defaults to the current financial year
 * rather than to everything, because "what do they owe" is almost always a
 * question about this year - and a customer of six years produces a scroll
 * nobody reads.
 */
export function CustomerLedgerPage() {
  const { customerId = '' } = useParams();
  const org = useActiveOrg();
  const startMonth = useFiscalYearStartMonth();
  const [period, setPeriod] = useState<Period>(() =>
    financialYearPeriod(financialYearOf(today(), 4).startYear, 4),
  );

  // A statement is in one currency; a customer invoiced in two has two,
  // and the picker between them appears only then.
  const [pickedCurrency, setPickedCurrency] = useState<string | null>(null);
  const perms = usePermissions();
  const [editing, setEditing] = useState(false);
  const query = useQuery({
    queryKey: ['ledger', org?.id, customerId, period.from, period.to, pickedCurrency],
    queryFn: () =>
      api.get<CustomerLedger>(`/customers/${customerId}/ledger${qs({ from: period.from, to: period.to, currency: pickedCurrency ?? undefined })}`),
    enabled: !!org?.id && !!customerId,
  });

  const currency = query.data?.customer.currency ?? 'INR';
  const totals = query.data?.totals;

  return (
    <>
      <PageHeader
        title={query.data?.customer.name ?? 'Statement of account'}
        subtitle={
          query.data?.customer.gstin
            ? `GSTIN ${query.data.customer.gstin}`
            : 'Invoices, payments and credits in date order.'
        }
        actions={
          <>
            <Button size="sm" variant="ghost" asChild>
              <Link to="/customers">
                <ArrowLeft className="size-4" aria-hidden />
                Customers
              </Link>
            </Button>
            {perms.can('customer', 'edit') && customerId ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="ledger-edit-customer">
                <Pencil className="size-4" aria-hidden />
                Edit customer
              </Button>
            ) : null}
          </>
        }
      />
      {editing ? <CustomerSheet customerId={customerId} onClose={() => setEditing(false)} onSaved={() => setEditing(false)} /> : null}

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-center gap-3">
          <PeriodPicker value={period} onChange={setPeriod} id="ledger-period" />
          {query.data && query.data.currencies.length > 1 ? (
            <div role="group" aria-label="Statement currency" className="flex gap-1" data-testid="ledger-currencies">
              {query.data.currencies.map((code) => (
                <button
                  key={code}
                  type="button"
                  aria-pressed={currency === code}
                  onClick={() => setPickedCurrency(code)}
                  className={
                    currency === code
                      ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                      : 'rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent'
                  }
                >
                  {code}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {totals ? (
          <div className="grid gap-3 sm:grid-cols-4">
            <Figure label="Invoiced" value={totals.invoiced} currency={currency} />
            <Figure label="Received" value={totals.received} currency={currency} />
            <Figure label="Credited" value={totals.credited} currency={currency} />
            <Figure label="Closing balance" value={totals.closing} currency={currency} emphasis />
          </div>
        ) : null}

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={6} cols={6} />
          ) : query.data?.entries.length ? (
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>Type</TH>
                  <TH>Reference</TH>
                  <TH>Details</TH>
                  <TH numeric>Debit</TH>
                  <TH numeric>Credit</TH>
                  <TH numeric>Balance</TH>
                </TR>
              </THead>
              <TBody>
                {query.data.entries.map((entry, index) => (
                  <TR key={`${entry.type}-${entry.reference}-${index}`}>
                    <TD className="whitespace-nowrap text-muted-foreground">{formatDate(entry.date)}</TD>
                    <TD>
                      <Badge variant={entry.type === 'payment' ? 'success' : entry.type === 'invoice' ? 'info' : 'neutral'}>
                        {TYPE_LABEL[entry.type]}
                      </Badge>
                    </TD>
                    <TD>
                      {entry.type === 'invoice' && entry.entityId ? (
                        <Link to={`/invoices/${entry.entityId}`} className="font-medium text-primary hover:underline">
                          {entry.reference}
                        </Link>
                      ) : (
                        <span className="font-medium">{entry.reference}</span>
                      )}
                    </TD>
                    <TD className="text-muted-foreground">{entry.description}</TD>
                    <TD numeric>
                      {Number(entry.debit) ? <Money value={entry.debit} currency={currency} /> : <span className="text-muted-foreground">—</span>}
                    </TD>
                    <TD numeric>
                      {Number(entry.credit) ? <Money value={entry.credit} currency={currency} /> : <span className="text-muted-foreground">—</span>}
                    </TD>
                    <TD numeric className="font-medium">
                      <Money value={entry.balance} currency={currency} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState
              icon={Scale}
              title="Nothing in this period"
              description="Issued invoices, payments and credit notes appear here. Drafts do not - they have not been sent."
            />
          )}
        </Card>

        {startMonth !== 4 ? (
          <p className="text-xs text-muted-foreground">
            Your financial year starts in month {startMonth}, so the year options above follow that.
          </p>
        ) : null}
      </div>
    </>
  );
}

function Figure({
  label,
  value,
  currency,
  emphasis,
}: {
  label: string;
  value: string;
  currency: string;
  emphasis?: boolean;
}) {
  return (
    <Card className="p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={emphasis ? 'mt-1 text-lg font-semibold tabular-nums' : 'mt-1 text-base tabular-nums'}>
        <Money value={value} currency={currency} />
      </p>
    </Card>
  );
}
